import { Box } from "@mui/material";
import { paletteVar, Typography } from "@platform/design-system";

import { HelpIcon } from "./primitives";

// Shared typography for the broker-page preferred-carrier zone cards. Kept
// separate from the primitives used by the stewardship-report cards so the
// zones can read at a larger, more legible scale without affecting those.
const ZONE_VALUE_SX = {
  fontSize: 24,
  fontWeight: 700,
  color: paletteVar("text-primary", "#00344f"),
  lineHeight: 1.3,
} as const;

const ZONE_ROW_VALUE_SX = {
  fontSize: 14,
  fontWeight: 700,
  color: paletteVar("text-primary", "#00344f"),
} as const;

function ZoneLabel({
  label,
  help,
}: {
  label: string;
  help?: string;
}): React.JSX.Element {
  return (
    <Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
      <Typography variant="body-medium" color="textSecondary">
        {label}
      </Typography>
      <HelpIcon label={label} description={help} />
    </Box>
  );
}

// A stacked stat: muted label above a prominent value, with an optional
// supporting caption beneath (e.g. a capture percentage).
function ZoneStat({
  label,
  value,
  caption,
  help,
}: {
  label: string;
  value: string;
  caption?: string;
  help?: string;
}): React.JSX.Element {
  return (
    <Box>
      <ZoneLabel label={label} help={help} />
      <Typography sx={ZONE_VALUE_SX}>{value}</Typography>
      {caption ? (
        <Typography variant="caption" color="textSecondary">
          {caption}
        </Typography>
      ) : null}
    </Box>
  );
}

// A compact stat: label on the left, value on the right, for secondary
// breakdown rows beneath a hero stat.
function ZoneRow({
  label,
  value,
  help,
}: {
  label: string;
  value: string | number;
  help?: string;
}): React.JSX.Element {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        minHeight: 24,
      }}
    >
      <ZoneLabel label={label} help={help} />
      <Typography sx={ZONE_ROW_VALUE_SX}>{value}</Typography>
    </Box>
  );
}

export { ZoneStat, ZoneRow };
